// TEMPLATE ADMINISTRATIVO PRINCIPAL
import React, { useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CachedIcon from '@mui/icons-material/Cached';
import { useSelector } from 'react-redux';
import PesquisarEstabelecimento from '../../utils/pesquisarEstabelecimento';
import AvatarAdmin from './avatarAdmin';
import CustonLoadingAremedGEstor from '../../load/custonLoadingAremedGEstor';
import MessageState from '../../messages/messageState';
import { isDesktop } from 'react-device-detect';
import ShortNome from '../../utils/ShortNome';
import { useLocation } from 'react-router-dom';
import NavLateralMaster from './navLateralMaster';

const DRAWER_WIDTH      = 265;
const MINI_DRAWER_WIDTH = 64;
const DRAWER_TRANSITION = 'width 225ms cubic-bezier(0,0,.2,1)';
const CONTENT_TRANSITION = 'margin 225ms cubic-bezier(0,0,.2,1)';

export default function TemplateAdmin({ element: component, ...rest }) {
    const url = useLocation();
    const user = useSelector(state => state.account.user);
    const departamento = useSelector(state => state.account.user?.departamento);
    const sessionTitle = useSelector(state => state.title.data);
    const configView = JSON.parse(localStorage.getItem("configView"));
    const [open, setOpen] = React.useState(configView ?? isDesktop);

    const handleDrawerToggle = () => {
        const next = !open;
        localStorage.setItem("configView", JSON.stringify(next));
        setOpen(next);
    };

    useEffect(() => {
        document.title = "GESTOR-ARMED";
    }, [url.pathname]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [url]);

    const drawerWidth = open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;

    return (
        <>
            {!user
                ? <CustonLoadingAremedGEstor open={true} />
                : <div className="tw-flex">
                    <CssBaseline />

                    {/* ────────────── NAVBAR ────────────── */}
                    <AppBar
                        position="fixed"
                        elevation={0}
                        style={{
                            backgroundColor: '#fff',
                            color: '#292b2b',
                            borderBottom: '1px solid #e5e7eb',
                            width: `calc(100% - ${drawerWidth}px)`,
                            marginLeft: drawerWidth,
                            transition: `${DRAWER_TRANSITION}, ${CONTENT_TRANSITION}`,
                        }}
                    >
                        <Toolbar
                            disableGutters
                            style={{ paddingLeft: 16, paddingRight: 16, minHeight: 64 }}
                        >
                            {/* Título */}
                            <div style={{ flex: 1, minWidth: 0 }} className="tw-hidden sm:tw-flex tw-items-center">
                                <Typography
                                    component="h1"
                                    style={{
                                        fontFamily: 'Work Sans, sans-serif',
                                        fontSize: 17,
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {sessionTitle ?? `GESTOR - ARMED / ${departamento}`}
                                </Typography>
                            </div>
                            <div className="sm:tw-hidden tw-flex-1" />

                            {/* Acções rápidas */}
                            <div className="tw-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0">
                                <PesquisarEstabelecimento />
                                <IconButton
                                    color="inherit"
                                    title="Limpar cache"
                                    onClick={() => window.location.reload()}
                                    size="small"
                                >
                                    <CachedIcon style={{ fontSize: 22 }} />
                                </IconButton>
                            </div>

                            {/* Separador */}
                            <div className="tw-flex-shrink-0" style={{ width: 1, height: 32, background: '#e5e7eb', margin: '0 10px' }} />

                            {/* Perfil */}
                            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                                <div className="tw-hidden md:tw-block tw-text-right">
                                    <p className="tw-text-sm tw-font-semibold tw-leading-tight tw-text-gray-800">
                                        <ShortNome nome={user?.dadosPessoais.nome} />
                                    </p>
                                    <p className="tw-text-xs tw-text-gray-500 tw-leading-tight">{user?.funcao}</p>
                                </div>
                                <AvatarAdmin />
                            </div>
                        </Toolbar>
                    </AppBar>

                    {/* ────────────── DRAWER LATERAL ────────────── */}
                    <Drawer
                        variant="permanent"
                        anchor="left"
                        sx={{
                            width: drawerWidth,
                            flexShrink: 0,
                            transition: DRAWER_TRANSITION,
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                background: '#85287e',
                                color: '#fff',
                                borderRight: 'none',
                                overflow: 'hidden',
                                transition: DRAWER_TRANSITION,
                                '&::-webkit-scrollbar': { width: '3px' },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#6A2065' },
                                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                            },
                        }}
                    >
                        {/* Cabeçalho do drawer */}
                        <div
                            className="tw-flex tw-items-center tw-bg-white tw-px-2 tw-flex-shrink-0"
                            style={{ minHeight: 64, borderBottom: '1px solid #e5e7eb' }}
                        >
                            {open && (
                                <img
                                    src="/img/logo/logoArmed.svg"
                                    alt="ARMED"
                                    height={38}
                                    className="tw-flex-1 tw-max-w-[140px] tw-object-contain"
                                />
                            )}
                            <IconButton
                                onClick={handleDrawerToggle}
                                size="small"
                                title={open ? 'Recolher menu' : 'Expandir menu'}
                                style={{ marginLeft: open ? 'auto' : 'auto', marginRight: open ? 0 : 'auto' }}
                            >
                                {open ? <MenuOpenIcon color="primary" /> : <MenuIcon color="primary" />}
                            </IconButton>
                        </div>

                        {/* Menu de navegação */}
                        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, paddingBottom: 32 }}>
                            <List sx={{ width: '100%' }} component="nav">
                                <NavLateralMaster open={open} />
                            </List>
                        </div>
                    </Drawer>

                    {/* ────────────── CONTEÚDO PRINCIPAL ────────────── */}
                    <main
                        className="tw-flex-grow tw-bg-[#ebebf4] tw-min-h-screen"
                        style={{
                            padding: (rest.paddingContent ?? 3) * 8,
                            paddingTop: 0,
                            transition: CONTENT_TRANSITION,
                        }}
                    >
                        <div style={{ minHeight: 64 }} />
                        {component}
                    </main>
                </div>
            }

            <MessageState />
        </>
    );
}
